import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

/**
 * Compact searchable multi-select for Reports filters.
 *
 * Renders a single button that summarises the current selection ("All",
 * "1 selected", or "3 selected"); opening the popover reveals a search field
 * and a scrollable option list. Selected items appear as small chips inside
 * the trigger button so users can deselect with one tap.
 *
 * Pure presentational — does NOT change filter logic. Reads & writes the same
 * shape that the existing pill buttons used (an array of selected values).
 */
export function MultiSelectFilter({
  label,
  options,           // [{ value, label }] OR ['raw_value', ...]
  selected,          // string[]
  onToggle,          // (value) => void
  emptyLabel = 'No options',
  testId,
  capitalize = false,
  locked = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const popRef = useRef(null);

  // Normalize to {value,label} regardless of input shape.
  const normalized = useMemo(() => (
    (options || []).map(o => (
      typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label ?? o.value }
    ))
  ), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(o => o.label.toLowerCase().includes(q));
  }, [normalized, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const summary = selected.length === 0
    ? 'All'
    : selected.length === 1
      ? (normalized.find(o => o.value === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border text-xs transition-colors ${
          selected.length > 0
            ? 'bg-[#D4AF37]/10 border-[#D4AF37]/60 text-[#D4AF37]'
            : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'
        }`}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${capitalize ? 'capitalize' : ''}`}>{summary}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Selected chips below button — one-tap remove */}
      {selected.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`${testId}-chips`}>
          {selected.map(v => {
            const lbl = normalized.find(o => o.value === v)?.label || v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggle(v)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 ${capitalize ? 'capitalize' : ''}`}
                data-testid={`${testId}-chip-${v}`}
                aria-label={`Remove ${lbl}`}
              >
                <span className="truncate max-w-[140px]">{lbl}</span>
                <X className="w-2.5 h-2.5" />
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm shadow-xl max-h-64 overflow-hidden flex flex-col"
          data-testid={`${testId}-popover`}
        >
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-[#1A1A1A] bg-[#050505]">
            <Search className="w-3 h-3 text-[#555]" />
            <input
              type="text"
              autoFocus
              placeholder={`Search ${label?.toLowerCase() || ''}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none text-xs text-[#EAE7E1] placeholder:text-[#444]"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto" role="listbox">
            {normalized.length === 0 && (
              <div className="px-3 py-3 text-xs text-[#555]">{emptyLabel}</div>
            )}
            {normalized.length > 0 && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-[#555]">No matches</div>
            )}
            {filtered.map(o => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onToggle(o.value)}
                  className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-xs transition-colors ${
                    on
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'text-[#AFAFAF] hover:bg-[#050505] hover:text-[#EAE7E1]'
                  } ${capitalize ? 'capitalize' : ''}`}
                  role="option"
                  aria-selected={on}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <span className="truncate">{o.label}</span>
                  {on && <Check className="w-3 h-3 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {locked && (
        <div className="text-[9px] text-[#D4AF37]/60 mt-1">Pro feature</div>
      )}
    </div>
  );
}
